import IMultiSelectWithCheckBox from '@components/multi-select/IMultiSelectWithCheckBox';
import PopUpBase from '@components/PopUpBase';
import PopUpWarning from '@components/PopUpWarning';
import StyledAutocomplete from '@components/styled-autocomplete';
import useStyledAutocomplete, { TypeReturn } from '@components/styled-autocomplete/useStyledAutocomplete';
import { pushError, pushSuccess } from '@components/toast';
import { AxiosError } from '@controllers/axiosClient';
import {
    cameraController,
    cameraTaskAttachmentHttpController,
    taskHttpController,
    taskTypeHttpController,
} from '@controllers/index';
import { defaultPagination, Paging } from '@Core';
import { IGetCameraProps } from '@EcamModel/controllers/ICameraHttpController';
import { Camera, CameraWithInfo, Location } from '@EcamModel/model';
import { CameraTask, TaskPriority, TaskStatus } from '@EcamModel/model/CameraTask';
import { CameraTaskAttachment } from '@EcamModel/model/CameraTaskAttachment';
import { CameraTaskType } from '@EcamModel/model/CameraTaskType';
import usePopUp, { IPopUp, UsePopUpReturnType } from '@hooks/usePopUp';
import { LoadingButton } from '@mui/lab';
import {
    Autocomplete,
    Button,
    CircularProgress,
    InputAdornment,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { validateDatePickerValue } from '@pages/cameras/add-new';
import { useCameraDetailContext } from '@pages/cameras/hook/CameraDetailsProvider';
import { CameraOverviewPopup } from '@pages/camerasOverview/configs';
import { autocompleteStyles } from '@pages/overview/smartFilterHighLight/components/BaseAutoCompleteForTypeMapping';
import { ellipsisStyles, LoadingProps } from '@pages/task-management';
import color from '@theme/Colors';
import _ from 'lodash';
import moment, { Moment } from 'moment';
import React, {
    Dispatch,
    KeyboardEvent,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { useBackdrop } from 'src/providers/BackdropProvider';
import { v4 as uuidv4 } from 'uuid';
import {
    AttachmentsFiles,
    cameraPriorityTask,
    cameraStatusTask,
    cameraSubTask,
    CameraSubTasks,
    mappingPriorityTask,
    mappingStatusTask,
    MAX_FILES,
    MAX_SIZE_MB,
} from '../../config';
import FormCreateAttachments from '../FormCreateAttachments';

type Props = IPopUp & {
    isShowTaskManagement?: boolean;
    isFetchingListTaskAttachments: boolean;
    isLoadingListTaskAttachments: boolean;
    cameraOverview: CameraOverviewPopup;
    locationAutocomplete: TypeReturn<
        Location,
        {
            search: any;
        }
    >;
    popupCompleteTask: UsePopUpReturnType;
    taskItem: CameraTask;
    listLocation: Paging<Location>;
    listTaskAttachments: Paging<CameraTaskAttachment> | undefined;
    attachmentsFileItem: AttachmentsFiles;
    uploadedAttachmentsFile: AttachmentsFiles[];
    onSetAttachmentsFileItem: (file: AttachmentsFiles) => void;
    setUploadedAttachmentsFile: Dispatch<SetStateAction<AttachmentsFiles[]>>;
    onSetTaskItem: (task: CameraTask) => void;
    onSetCameraOverview: Dispatch<SetStateAction<CameraOverviewPopup>>;
};

type FormValues = {
    location?: any;
    camera?: Camera;
    priority?: TaskPriority;
    status: TaskStatus;
    subTask?: CameraSubTasks[];

    taskName: string;
    problem?: string;
    solution?: string;
    assign?: string;

    createDate?: Moment;
    endDate: Moment;
    faultStartDate?: Moment;
};

export default function PopupUpsertTask(props: Props) {
    const {
        open,
        isFetchingListTaskAttachments,
        isLoadingListTaskAttachments,
        isShowTaskManagement,
        cameraOverview,
        locationAutocomplete,
        popupCompleteTask,
        taskItem,
        listTaskAttachments,
        listLocation,
        attachmentsFileItem,
        uploadedAttachmentsFile,
        onSetAttachmentsFileItem,
        setUploadedAttachmentsFile,
        onSetTaskItem,
        onSetCameraOverview,
        onClose,
    } = props;
    const backdrop = useBackdrop();
    const queryClient = useQueryClient();
    const listAttachmentsRef = useRef<AttachmentsFiles[]>([]);
    const isMobile = useMediaQuery('(max-width: 490px)');

    const popupConfirmDeleteAttachmentsFile = usePopUp();
    const popupConfirmUpsertDescriptionAttachmentsFile = usePopUp();
    const { refetchTrailAuditEvent } = useCameraDetailContext();

    const [loadingState, setLoadingState] = useState<LoadingProps>({
        getData: true,
        popupConfirm: false,
    });

    const [page, setPage] = useState<number>(1);
    const maxTotalPagesRef = useRef<number>(page);

    const [listTaskType, setListTaskType] = useState<Paging<CameraTaskType>>(defaultPagination());
    const debounceRef = useRef<_.DebouncedFunc<(value: string) => void>>();

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [attachmentsDescription, setAttachmentsDescription] = useState<string | undefined>(
        attachmentsFileItem.Desc || undefined
    );

    const priorityTaskValue: TaskPriority = cameraPriorityTask?.find(
        (item) => item.Value === taskItem.TaskPriority
    )?.Value;

    const statusTaskValue: TaskStatus = cameraStatusTask?.find((item) => item.Value === taskItem.TaskStatus)?.Value;

    const specificSubTasks: CameraSubTasks[] = cameraSubTask.filter((task) =>
        [taskItem.SubTask].some((type) => type?.includes(task.Name))
    );
    const parsedSubTasks: CameraSubTasks[] = JSON.parse(taskItem.SubTask ?? '[]');

    const updatedSubTasks: CameraSubTasks[] = specificSubTasks.map((task) => {
        const existingTask = parsedSubTasks.find((t) => t.Name === task.Name);
        return {
            ...task,
            Status: existingTask ? existingTask.Status : task.Status,
        };
    });

    const valuesDefault: FormValues = {
        location: undefined,
        camera: taskItem.Camera ?? undefined,
        priority: priorityTaskValue ?? cameraPriorityTask[1].Value,
        status: statusTaskValue ?? cameraStatusTask[0].Value,
        taskName: taskItem.TaskName ?? undefined,
        assign: taskItem?.Assignee ?? undefined,
        problem: taskItem.Problem ?? undefined,
        solution: taskItem.Solution ?? undefined,
        createDate: moment(taskItem.Created) ?? moment(),
        endDate: taskItem.EndDate ? moment(taskItem.EndDate) : moment(),
        faultStartDate: taskItem.FaultStartDate ? moment(taskItem.FaultStartDate) : moment(),
        subTask: updatedSubTasks,
    };

    const {
        handleSubmit,
        watch,
        reset,
        control,
        setValue,
        setError,
        clearErrors,
        getValues,
        formState: { isDirty, isValid, errors, dirtyFields, isSubmitting },
    } = useForm<FormValues>({
        mode: 'all',
        defaultValues: valuesDefault,
    });

    const listCameraAutocomplete = useStyledAutocomplete({
        async getList(props: IGetCameraProps): Promise<Paging<CameraWithInfo>> {
            if (watch()?.location?.Id) {
                const res = await cameraController.listCameraAbleToCreateTask(props).finally(() => { });
                const findNameCamera = res.rows?.find((c) => c.Id === cameraOverview.cameraId);
                onSetCameraOverview((prev) => ({
                    ...prev,
                    cameraName: findNameCamera?.Name || '',
                }));
                return res;
            }
            return defaultPagination();
        },
        listProps: {
            LocationId: Number(watch()?.location?.Id) || undefined,
            search: { fields: ['Name'] } as any,
        },
        dependencyList: [watch()?.location?.Id],
    });

    const isListAttachmentsDisabled = useMemo(() => {
        return JSON.stringify(listAttachmentsRef.current) === JSON.stringify(uploadedAttachmentsFile);
    }, [listAttachmentsRef.current, uploadedAttachmentsFile]);

    const disabled = !(isValid && isDirty);

    const setPartialLoading = (loadings: Partial<LoadingProps>) => {
        setLoadingState((prev) => ({ ...prev, ...loadings }));
    };

    const handleCancel = () => {
        onClose?.();
        onSetTaskItem({} as CameraTask);
        setPage(1);
        setListTaskType(defaultPagination());
        setUploadedAttachmentsFile([]);
    };

    const onChangeLocation = (l: any) => {
        setValue('location', l);
        setValue('camera', undefined);
    };

    const upsertCameraTaskAPI = async (_task: CameraTask) => {
        const response = await taskHttpController.upsert(_task);
        return response;
    };

    const mutationUpsertCameraNote = useMutation(upsertCameraTaskAPI, {
        onSuccess: async (res, variables) => {
            if (uploadedAttachmentsFile?.some((file) => file.fileUrl)) {
                await handleUpsertTaskAttachment(uploadedAttachmentsFile, res.Id!);
                await queryClient.invalidateQueries(['listTaskAttachments']);
            }

            pushSuccess(variables.Id ? 'Task update successfully' : 'Task created successfully');
            queryClient.invalidateQueries(['listTask']);
            if (props.isShowTaskManagement) {
                await refetchTrailAuditEvent();
            }
        },
        onError: (err: AxiosError) => pushError(err.message),
        onSettled: () => {
            setPartialLoading({ popupConfirm: false });
        },
    });

    const handleConfirmUpsertTask = async (data: FormValues) => {
        setPartialLoading({ popupConfirm: true });
        const _task: CameraTask = {
            Id: taskItem.Id || undefined,
            CameraId: data?.camera?.Id!,
            Assignee: data?.assign,
            TaskName: data?.taskName.trim(),
            TaskPriority: data?.priority!,
            Problem: data?.problem,
            Solution: data?.solution,
            TaskStatus: data?.status === TaskStatus.Completed ? taskItem.TaskStatus : data.status,
            EndDate: moment(data?.endDate).toDate(),
            // Created: moment(data?.createDate).toDate(),
            FaultStartDate: moment(data?.faultStartDate)?.toDate(),
            SubTask:
                JSON.stringify(data?.subTask) !== JSON.stringify(valuesDefault.subTask)
                    ? JSON.stringify(data?.subTask)
                    : undefined,
        };

        await mutationUpsertCameraNote.mutateAsync(_task).finally(() => handleCancel?.());
    };

    const handleGetOptionTaskTypes = (valueSearch?: string) => {
        setPartialLoading({ getData: true });

        taskTypeHttpController
            .list({
                pageSize: 12,
                page: page || 1,
                search: {
                    fields: ['Name'],
                    content: valueSearch || '',
                },
            })
            .then((res) => {
                if (res) {
                    maxTotalPagesRef.current = Math.max(maxTotalPagesRef.current, res.totalPages);

                    setListTaskType((prevList) => {
                        const newRows = res.rows.filter(
                            (newItem) => !prevList.rows.some((existingItem) => existingItem.Id === newItem.Id)
                        );
                        return {
                            ...res,
                            rows: [...prevList.rows, ...newRows],
                        };
                    });
                }
            })
            .finally(() => {
                setPartialLoading({ getData: false });
            });
    };

    const handleAddTaskType = (value: string) => {
        taskTypeHttpController
            .upsert({
                Name: value,
            })
            .then((res) => {
                setListTaskType((prev) => ({
                    ...prev,
                    rows: prev.rows.some((task) => task.Id === res.Id)
                        ? prev.rows.map((task) => (task.Id === res.Id ? res : task))
                        : [...prev.rows, res],
                }));
            })
            .catch((err) => { })
            .finally(() => { });
    };

    useEffect(() => {
        debounceRef.current = _.debounce((inputValue: string) => {
            const hasDuplicate = listTaskType.rows?.some(
                (item) => item.Name.toLowerCase() === inputValue.toLowerCase()
            );
            if (!hasDuplicate && page < maxTotalPagesRef.current) {
                handleGetOptionTaskTypes(inputValue);
            }
        }, 1000);
    }, [page]);

    const handleInputChange = useCallback((event: React.SyntheticEvent, value: string) => {
        debounceRef.current?.(value);
    }, []);

    // Upload attachments
    const handleChangeAttachmentsFile = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files) return;
        const selectedFiles = Array.from(event.target.files);

        const validFiles = selectedFiles.filter((file) => {
            const validExtensions = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];

            const isValidExtension = validExtensions.includes(file.type);
            const isValidSize = file.size <= MAX_SIZE_MB * 1024 * 1024;

            if (!isValidExtension) {
                pushError(`File "${file.name}" has an invalid type.`);
            }

            if (!isValidSize) {
                pushError(`File "${file.name}" exceeds the maximum size of ${MAX_SIZE_MB}MB.`);
            }

            return isValidExtension && isValidSize;
        });

        const newFiles: AttachmentsFiles[] = validFiles
            .filter((file) => {
                const isDuplicate = (uploadedAttachmentsFile || [])?.some(
                    (uploadedFile) =>
                        uploadedFile.fileUrl &&
                        uploadedFile.fileUrl.name === file.name &&
                        uploadedFile.fileUrl.size === file.size &&
                        uploadedFile.fileUrl.type === file.type
                );

                if (isDuplicate) {
                    pushError(`File "${file.name}" is already uploaded.`);
                }

                return !isDuplicate;
            })
            .map((file) => ({
                _fakeId: uuidv4(),
                Desc: undefined,
                fileUrl: file,
            }));

        const combinedFiles = [...(uploadedAttachmentsFile || []), ...newFiles];

        if (combinedFiles.length > MAX_FILES) {
            pushError(`You can upload a maximum of ${MAX_FILES} images.`);
            return;
        }

        setUploadedAttachmentsFile(combinedFiles);
    };

    // Delete attachments
    const handleDeleteAttachmentsFile = (idFile: string) => {
        setUploadedAttachmentsFile((prevFiles) => prevFiles.filter((_, i) => _._fakeId !== idFile));
    };

    // Cancel change description for attachment
    const handleCancelChangeDescription = (isCheckEscape: boolean) => {
        setIsEditing(false);
        setAttachmentsDescription(isCheckEscape ? attachmentsFileItem.Desc : attachmentsDescription);
        setUploadedAttachmentsFile((prevFile) => {
            const updatedAttachmentFileItem = prevFile.map((_prFile) => {
                if (_prFile._fakeId === attachmentsFileItem._fakeId) {
                    return {
                        ..._prFile,
                        Desc: isCheckEscape ? _prFile.Desc : attachmentsDescription || undefined,
                    };
                }
                return _prFile;
            });
            return updatedAttachmentFileItem;
        });
    };

    // Update status for description
    const handleClickUpsertDescription = (file: AttachmentsFiles) => {
        if (isEditing) return;
        onSetAttachmentsFileItem(file);
        setAttachmentsDescription(file.Desc || undefined);
        setIsEditing(true);
    };

    // Change description for attachment
    const handleTypingChangeDescription = (e: React.ChangeEvent<HTMLInputElement>) => {
        setAttachmentsDescription(e.target.value);
    };

    // Confirm change description for attachment
    const handleSetAttachmentsDescription = (fileId: number) => {
        if (fileId) {
            backdrop.setTrue();
            cameraTaskAttachmentHttpController
                .upsert({ Desc: attachmentsDescription?.trim() || '', Id: fileId })
                .then((_) => {
                    queryClient.invalidateQueries(['listTaskAttachments']);
                    pushSuccess('Change description for attachment successfully');
                })
                .catch((err) => pushError(err.message))
                .finally(() => {
                    setIsEditing(false);
                    backdrop.setFalse();
                });
        } else {
            handleCancelChangeDescription(false);
        }
    };

    // Blur text field attachments
    const handleBlurTextField = (fileId: number) => {
        if (attachmentsDescription !== attachmentsFileItem.Desc && attachmentsDescription !== undefined && fileId) {
            popupConfirmUpsertDescriptionAttachmentsFile.setTrue();
        } else {
            handleCancelChangeDescription(fileId ? true : false);
        }
    };

    // Keyboard event for attachments
    const handleKeyboardEvent = (e: KeyboardEvent<HTMLDivElement>, fileId: number) => {
        if (e.key === 'Enter') {
            handleSetAttachmentsDescription(fileId);
        } else if (e.key === 'Escape') {
            handleCancelChangeDescription(true);
        }
    };

    // Confirm upsert task attachments
    const handleUpsertTaskAttachment = async (attachments: AttachmentsFiles[], taskId: number) => {
        const formData = new FormData();

        attachments.forEach((file, i) => {
            if (file.fileUrl) {
                formData.append(`CameraTaskAttachmentFile`, file.fileUrl);
            }
        });

        formData.append(`Desc`, attachments?.filter((a) => !a.Id)?.map((attachment) => attachment.Desc || '') as any);
        formData.append(`CameraTaskId`, taskId.toString()!);

        await cameraTaskAttachmentHttpController.createCameraTaskAttachments(formData).then((_) => { });
    };

    // Confirm delete task attachments
    const handleConfirmDeleteAttachmentsFile = () => {
        setPartialLoading({ popupConfirm: true });
        cameraTaskAttachmentHttpController
            .delete(attachmentsFileItem.Id?.toString()!)
            .then(async (_) => {
                queryClient.invalidateQueries(['listTaskAttachments']);
                queryClient.invalidateQueries(['listTask']);
                pushSuccess('Deleted attachment successfully');
                popupConfirmDeleteAttachmentsFile.onClose();
            })
            .catch((err) => {
                pushError(err.response?.data?.message);
            })
            .finally(() => {
                setPartialLoading({ popupConfirm: false });
            });
    };

    // Confirm upsert desc popup
    const handleConfirmUpsertDescriptionAttachmentsFile = () => {
        setPartialLoading({ popupConfirm: true });
        cameraTaskAttachmentHttpController
            .upsert({ Desc: attachmentsDescription!, Id: attachmentsFileItem.Id })
            .then((_) => {
                setIsEditing(false);
                queryClient.invalidateQueries(['listTaskAttachments']);
                pushSuccess('Change description for attachment successfully');
                popupConfirmUpsertDescriptionAttachmentsFile.onClose();
            })
            .catch((err) => pushError(err.message))
            .finally(() => {
                setPartialLoading({ popupConfirm: false });
            });
    };

    // Cancel upsert desc popup
    const handleCancelUpsertDescriptionAttachmentsFile = () => {
        setIsEditing(false);
        setAttachmentsDescription(attachmentsFileItem.Desc);
        setUploadedAttachmentsFile((prevFile) => {
            const updatedAttachmentFileItem = prevFile.map((_prFile) => {
                if (_prFile.Id === attachmentsFileItem.Id) {
                    return {
                        ..._prFile,
                        Desc: _prFile.Desc || undefined,
                    };
                }
                return _prFile;
            });
            return updatedAttachmentFileItem;
        });
        popupConfirmUpsertDescriptionAttachmentsFile.onClose();
    };

    useEffect(() => {
        if (!open) return;
        reset(valuesDefault);
    }, [open]);

    useEffect(() => {
        if (!open && !listTaskAttachments?.rows.length) return;

        const listUploadAttachments = listTaskAttachments?.rows.map(
            (item) =>
            ({
                ...item,
            } as AttachmentsFiles)
        )!;

        setUploadedAttachmentsFile(listUploadAttachments);

        listAttachmentsRef.current = listUploadAttachments;
    }, [open, listTaskAttachments]);

    useEffect(() => {
        if (!open) return;
        handleGetOptionTaskTypes();
    }, [open, page]);

    useEffect(() => {
        if (!open) return;

        const findZones = listLocation.rows?.find((item) => item.Id === taskItem.Camera?.Zone?.Location?.Id);
        console.log('🚀 ~ useEffect ~ findZones:', findZones);
        if (findZones) {
            reset({ ...valuesDefault, location: findZones });
            onSetCameraOverview((prev) => ({
                ...prev,
                locationName: findZones.Name,
            }));
        }
    }, [open, taskItem, listLocation]);

    return (
        <PopUpBase
            title={
                <Typography
                    variant="h4"
                    sx={{
                        ...ellipsisStyles,
                    }}
                >
                    {taskItem.TaskName ? `Edit task: ${taskItem.TaskName}` : 'Create task'}
                </Typography>
            }
            dialogProps={{
                fullWidth: true,
                sx: {
                    '& .MuiDialog-paper': {
                        borderRadius: '10px',
                        maxWidth: 750,
                    },
                    zIndex: (theme) => theme.zIndex.modal,
                },
            }}
            fixOverflow
            open={open}
            onClose={onClose}
            // hideConfirm
            minWidthButton={130}
            desc={
                <Stack
                    sx={{
                        maxHeight: { xs: 490, sm: 610, md: 730 },
                        overflowY: 'auto',
                        overflowX: 'auto',
                        '::-webkit-scrollbar': {
                            width: '2px',
                        },
                    }}
                    direction={'column'}
                    spacing={2}
                    bgcolor={color.grey100}
                    borderRadius={'4px'}
                    p={{ xs: 1, sm: 2 }}
                >
                    {/* location - camera */}
                    <Stack
                        direction={'row'}
                        spacing={{ xs: 0, sm: 2 }}
                        rowGap={{ xs: 2, sm: 0 }}
                        width={'100%'}
                        flexWrap={{ xs: 'wrap', sm: 'inherit' }}
                    >
                        <Stack width={{ xs: '100%', sm: '50%' }}>
                            {isShowTaskManagement ? (
                                <TextField
                                    label="Location"
                                    required
                                    value={cameraOverview.locationName}
                                    rows={6}
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                />
                            ) : (
                                <Controller
                                    name="location"
                                    control={control}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: 'Please select a location.',
                                        },
                                    }}
                                    render={({ field, fieldState: { error } }) => (
                                        <StyledAutocomplete
                                            {...locationAutocomplete}
                                            {...field}
                                            getOptionLabel={(o) => o.Name}
                                            label={
                                                <span>
                                                    Location<span style={{ color: 'red' }}>{`*`}</span>
                                                </span>
                                            }
                                            placeholder="Select location"
                                            disabledAllOption
                                            value={field.value}
                                            onChange={(newValue) => {
                                                setValue('location', newValue, {
                                                    shouldValidate: true,
                                                    shouldTouch: true,
                                                    shouldDirty: true,
                                                });
                                                onChangeLocation(newValue!);
                                            }}
                                            wrapperWidth="100%"
                                        />
                                    )}
                                />
                            )}
                        </Stack>

                        <Stack width={{ xs: '100%', sm: '50%' }}>
                            {isShowTaskManagement ? (
                                <TextField
                                    label="Camera"
                                    required
                                    value={cameraOverview.cameraName || 'Loading...'}
                                    disabled={!cameraOverview.cameraName}
                                    rows={6}
                                    InputProps={{
                                        readOnly: true,
                                        endAdornment: !cameraOverview.cameraName && (
                                            <InputAdornment position="end">
                                                <CircularProgress size={16} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            ) : (
                                <Controller
                                    name="camera"
                                    control={control}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: 'Please select a camera.',
                                        },
                                    }}
                                    render={({ field, fieldState: { error } }) => (
                                        <StyledAutocomplete
                                            {...listCameraAutocomplete}
                                            {...field}
                                            getOptionLabel={(o) => o.Name}
                                            label={
                                                <span>
                                                    Camera<span style={{ color: 'red' }}>{`*`}</span>
                                                </span>
                                            }
                                            placeholder="Select camera"
                                            disabledAllOption
                                            value={watch().camera}
                                            onChange={(newValue) => {
                                                setValue('camera', newValue, {
                                                    shouldValidate: true,
                                                    shouldTouch: true,
                                                    shouldDirty: true,
                                                });
                                            }}
                                            wrapperWidth="100%"
                                        />
                                    )}
                                />
                            )}
                        </Stack>
                    </Stack>

                    {/* task name */}
                    <Stack width={'100%'}>
                        <Controller
                            name="taskName"
                            control={control}
                            rules={{
                                required: {
                                    value: true,
                                    message: 'Please enter a task.',
                                },
                                maxLength: {
                                    value: 255,
                                    message: 'Problem should not exceed 255 characters.',
                                },
                            }}
                            render={({ field, fieldState: { error } }) => (
                                <TextField
                                    {...field}
                                    required
                                    autoFocus
                                    placeholder={'Task'}
                                    label="Task"
                                    error={!!error?.message}
                                    helperText={error?.message}
                                    value={field.value}
                                />
                            )}
                        />
                    </Stack>

                    <Stack width={'100%'}>
                        <Controller
                            name="subTask"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <IMultiSelectWithCheckBox
                                    {...field}
                                    limitTags={3}
                                    label={'Sub task'}
                                    keyId={'Name'}
                                    keyLabel={'Name'}
                                    placeHolder="Select sub task"
                                    value={field.value}
                                    listData={cameraSubTask}
                                    setFilter={field.onChange}
                                    chipSize={250}
                                />
                            )}
                        />
                    </Stack>

                    {/* problem */}
                    {/* <Stack width={'100%'}>
                        <Controller
                            name="taskType"
                            control={control}
                            render={({ field, fieldState: { error } }) => {
                                return (
                                    <>
                                        <ICustomFreeSoloMultiSelectWithCheckBox
                                            pageNumber={page}
                                            totalPages={listTaskType.totalPages}
                                            options={listTaskType.rows}
                                            loading={loadingState.getData}
                                            placeholder="Select sub tasks"
                                            label="Sub task"
                                            value={field.value || []}
                                            onChangeValue={field.onChange}
                                            getOptionLabel={(option) => option.Name || ''}
                                            onSetPage={setPage}
                                            onInputChange={handleInputChange}
                                            handleAddItem={handleAddTaskType}
                                        />
                                    </>
                                );
                            }}
                        />
                    </Stack> */}

                    {/* createDate - endDate - assign  */}
                    <Stack
                        direction={'row'}
                        spacing={{ xs: 0, sm: 2 }}
                        rowGap={{ xs: 2, sm: 0 }}
                        width={'100%'}
                        flexWrap={{ xs: 'wrap', sm: 'inherit' }}
                    >
                        {/* <Stack width={'50%'}>
                            <Controller
                                name="createDate"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <DesktopDatePicker
                                        {...field}
                                        label={
                                            <span>
                                                Create date<span style={{ color: 'red' }}>{`*`}</span>
                                            </span>
                                        }
                                        value={field.value}
                                        readOnly
                                        format="DD/MM/YYYY"
                                        slotProps={{
                                            textField: {
                                                inputProps: {
                                                    readOnly: true,
                                                },
                                                fullWidth: true,
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Stack> */}
                        <Stack width={{ xs: '100%', sm: '50%' }}>
                            <Controller
                                name="endDate"
                                control={control}
                                rules={{
                                    required: {
                                        value: true,
                                        message: 'Please choose a date.',
                                    },
                                }}
                                render={({ field, fieldState: { error } }) => (
                                    <DesktopDatePicker
                                        {...field}
                                        label={
                                            <span>
                                                Due date<span style={{ color: 'red' }}>{`*`}</span>
                                            </span>
                                        }
                                        value={field.value}
                                        format="DD/MM/YYYY"
                                        shouldDisableDate={(date) =>
                                            validateDatePickerValue(moment(date).toDate()) !== null
                                        }
                                        slotProps={{
                                            textField: {
                                                inputProps: {
                                                    readOnly: true,
                                                },
                                                fullWidth: true,
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Stack>
                        <Stack width={{ xs: '100%', sm: '50%' }} direction={'column'} spacing={1}>
                            <Controller
                                name="assign"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        placeholder={'Assignee'}
                                        label="Assignee"
                                        error={!!error?.message}
                                        helperText={error?.message}
                                        value={field.value}
                                    />
                                )}
                            />
                        </Stack>
                    </Stack>

                    {/* faultStartDate - taskType */}
                    {/* <Stack direction={'row'} spacing={2} width={'100%'}>
                        <Stack width={'50%'}>
                            <Controller
                                name="faultStartDate"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <DesktopDatePicker
                                        {...field}
                                        label={<span>Fault start date</span>}
                                        value={field.value}
                                        format="DD/MM/YYYY"
                                        shouldDisableDate={(date) => disableFutureDates(moment(date).toDate())}
                                        slotProps={{
                                            textField: {
                                                inputProps: {
                                                    readOnly: true,
                                                },
                                                fullWidth: true,
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Stack>
                    </Stack> */}

                    {/* status - priority */}
                    <Stack
                        direction={'row'}
                        spacing={{ xs: 0, sm: 2 }}
                        rowGap={{ xs: 2, sm: 0 }}
                        width={'100%'}
                        flexWrap={{ xs: 'wrap', sm: 'inherit' }}
                    >
                        <Stack width={{ xs: '100%', sm: '50%' }}>
                            <Controller
                                name="status"
                                control={control}
                                rules={{
                                    required: true,
                                }}
                                render={({ field, fieldState: { error } }) => (
                                    <Autocomplete
                                        {...field}
                                        options={
                                            !Object.keys(taskItem)?.length
                                                ? cameraStatusTask
                                                    .filter(
                                                        (task) =>
                                                            task.Value !== TaskStatus.Completed &&
                                                            task.Value !== TaskStatus.Cancelled
                                                    )
                                                    .map((option) => option.Value)
                                                : cameraStatusTask.map((option) => option.Value)
                                        }
                                        value={field.value}
                                        getOptionLabel={(option) => mappingStatusTask[option]}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                InputLabelProps={{ shrink: true }}
                                                label={
                                                    <span>
                                                        Status<span style={{ color: 'red' }}>{`*`}</span>
                                                    </span>
                                                }
                                                placeholder="Select status"
                                            />
                                        )}
                                        clearIcon={<></>}
                                        sx={{
                                            ...autocompleteStyles,
                                            '& .MuiAutocomplete-endAdornment': {
                                                top: '50%',
                                            },
                                        }}
                                        onChange={(_, val) => {
                                            field.onChange(val);
                                        }}
                                    />
                                )}
                            />
                        </Stack>

                        <Stack width={{ xs: '100%', sm: '50%' }}>
                            <Controller
                                name="priority"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <Autocomplete
                                        {...field}
                                        options={cameraPriorityTask.map((option) => option.Value)}
                                        value={field.value}
                                        getOptionLabel={(option) => mappingPriorityTask[option]}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                InputLabelProps={{ shrink: true }}
                                                label={<span>Priority</span>}
                                                placeholder="Select priority"
                                            />
                                        )}
                                        clearIcon={<></>}
                                        sx={{
                                            ...autocompleteStyles,
                                            '& .MuiAutocomplete-endAdornment': {
                                                top: '50%',
                                            },
                                        }}
                                        onChange={(_, val) => {
                                            field.onChange(val);
                                        }}
                                    />
                                )}
                            />
                        </Stack>
                    </Stack>

                    {!isShowTaskManagement && (
                        <>
                            {/* problem */}
                            <Stack width={'100%'}>
                                <Controller
                                    name="problem"
                                    control={control}
                                    rules={{
                                        maxLength: {
                                            value: 1000,
                                            message: 'Problem should not exceed 1000 characters.',
                                        },
                                    }}
                                    render={({ field, fieldState: { error } }) => (
                                        <TextField
                                            {...field}
                                            label="Problem"
                                            error={!!error?.message}
                                            helperText={error?.message}
                                            multiline
                                            sx={{
                                                '& .MuiInputBase-input': {
                                                    padding: 0,
                                                    borderRadius: 0,
                                                },
                                            }}
                                            rows={6}
                                            value={field.value}
                                            onChange={field.onChange}
                                            placeholder="Enter problem"
                                        />
                                    )}
                                />
                                <Typography
                                    fontSize={12}
                                    color={watch()?.problem?.length! > 1000 ? color.danger : color.textPrimary}
                                    mt={'4px'}
                                    textAlign={'end'}
                                >
                                    {watch().problem?.length ?? 0} / 1000
                                </Typography>
                            </Stack>

                            {/* solution */}
                            <Stack width={'100%'}>
                                <Controller
                                    name="solution"
                                    control={control}
                                    rules={{
                                        maxLength: {
                                            value: 1000,
                                            message: 'Solution should not exceed 1000 characters.',
                                        },
                                    }}
                                    render={({ field, fieldState: { error } }) => (
                                        <TextField
                                            {...field}
                                            label="Solution"
                                            error={!!error?.message}
                                            helperText={error?.message}
                                            multiline
                                            sx={{
                                                '& .MuiInputBase-input': {
                                                    padding: 0,
                                                    borderRadius: 0,
                                                },
                                            }}
                                            rows={6}
                                            value={field.value}
                                            onChange={field.onChange}
                                            placeholder="Enter solution"
                                        />
                                    )}
                                />
                                <Typography
                                    fontSize={12}
                                    color={watch()?.solution?.length! > 1000 ? color.danger : color.textPrimary}
                                    mt={'4px'}
                                    textAlign={'end'}
                                >
                                    {watch().solution?.length ?? 0} / 1000
                                </Typography>
                            </Stack>

                            <FormCreateAttachments
                                isEditing={isEditing}
                                loadingState={isLoadingListTaskAttachments || isFetchingListTaskAttachments}
                                attachmentsFileItem={attachmentsFileItem}
                                attachmentsDescription={attachmentsDescription}
                                uploadedAttachmentsFile={uploadedAttachmentsFile}
                                popupConfirmDeleteAttachmentsFile={popupConfirmDeleteAttachmentsFile}
                                onSetAttachmentsFileItem={onSetAttachmentsFileItem}
                                handleKeyboardEvent={handleKeyboardEvent}
                                handleBlurTextField={handleBlurTextField}
                                handleDeleteAttachmentsFile={handleDeleteAttachmentsFile}
                                handleChangeAttachmentsFile={handleChangeAttachmentsFile}
                                handleClickUpsertDescription={handleClickUpsertDescription}
                                handleTypingChangeDescription={handleTypingChangeDescription}
                            />
                        </>
                    )}

                    <PopUpWarning
                        {...popupConfirmDeleteAttachmentsFile}
                        isLoading={loadingState.popupConfirm}
                        title="Confirm"
                        message={`Are you sure you want to delete this attachment for this task: ${taskItem.TaskName}?`}
                        onClose={() => {
                            onSetAttachmentsFileItem({} as AttachmentsFiles);
                            popupConfirmDeleteAttachmentsFile.onClose();
                        }}
                        onConfirm={handleConfirmDeleteAttachmentsFile}
                    />

                    <PopUpWarning
                        {...popupConfirmUpsertDescriptionAttachmentsFile}
                        isLoading={loadingState.popupConfirm}
                        title="Confirm"
                        message={`Are you sure you want to change the description from "${attachmentsFileItem.Desc || '""'
                            }" to "${attachmentsDescription || ''}"?`}
                        onClose={handleCancelUpsertDescriptionAttachmentsFile}
                        onConfirm={handleConfirmUpsertDescriptionAttachmentsFile}
                    />
                </Stack>
            }
            customActions={
                <Stack direction={'row'} width="100%" justifyContent="space-between">
                    <Button
                        variant="cancel"
                        onClick={() => {
                            handleCancel();
                        }}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        loading={loadingState.popupConfirm}
                        loadingPosition="center"
                        variant="contained"
                        disabled={
                            (!Object.keys(taskItem).length ? disabled : disabled && isListAttachmentsDisabled) ||
                            loadingState.popupConfirm
                        }
                        onClick={handleSubmit((data) => {
                            if (data.status === TaskStatus.Completed) {
                                popupCompleteTask.setTrue();
                                onClose?.();
                            } else {
                                handleConfirmUpsertTask(data);
                            }
                        })}
                    >
                        {!Object.keys(taskItem)?.length ? 'Create' : 'Confirm'}
                    </LoadingButton>
                </Stack>
            }
        />
    );
}
